import type { DocumentPredictedType, PredictedType } from "@instacover/instacar-database/browser"

import { CarViewPredictResponse, DocumentPredictResponse } from "../../../../helpers/model/modelTypes"
import { MarkerInput, PhotoLocation, PhotoType, UserModelMatch } from "../../../../utils/types"
import { dexie } from "../../db"
import { logDexieError } from "../../utils/logDexieError"

// ─── Types ─────────────────────────────────────────────────────────────────────

/** Binary data fields stored in Dexie but excluded from queries that don't need them */
export type PhotoBinaryFields = {
  // Raw bytes for locally captured photos
  data?: Uint8Array
  mime?: string

  // Thumbnail bytes (locally captured)
  thumbnailData?: Uint8Array
  thumbnailMime?: string
}

/** Binary photo payload stored in its own table (photoBlobs); `photoId` is a unique 1:1 ref to PhotoSlim.id. */
export type PhotoBlob = { dexieId: number; photoId: string; sessionId: string } & PhotoBinaryFields

/** Photo metadata without binary data — used for rendering (lists, final-check, overview) */
export type PhotoSlim = {
  dexieId: number
  id: string
  sessionId: string
  type: PhotoType

  createdAt: Date
  updatedAt: Date
  uploadedAt?: Date

  deleted?: boolean
  // Set when a server delete-sync attempt failed
  deleteFailed?: boolean

  // Presigned server URL — set after upload or when opening a session on another device
  url?: string
  thumbnailUrl?: string

  skipped?: boolean
  markers?: MarkerInput[]

  // Set on a view photo copied from a Claim photo
  sourcePhotoId?: string

  // Upload state (replaces Jotai pending state)
  uploadProgress: number // -1 = waiting, 0-100 = uploading, 101 = done
  uploadFailed: boolean
  retries: number

  // AI metadata (from PendingPhotoWithImage)
  carViewPredictedType?: PredictedType | null
  documentPredictedType?: DocumentPredictedType | null
  location?: PhotoLocation | null
  userModelMatch?: UserModelMatch | null
  carViewBrowserModelResult?: CarViewPredictResponse | null
  documentBrowserModelResult?: DocumentPredictResponse | null

  // Whether a local binary exists in photoBlobs (persisted on the metadata row)
  hasData: boolean
  hasThumbnailData: boolean
}

/** Photo metadata + binary payload */
export type PhotoWithBinaryData = PhotoSlim & PhotoBinaryFields

/** Input for upsertPhoto — metadata + binaries, without the auto key or the derived flags */
export type UpsertPhotoInput = Omit<PhotoWithBinaryData, "dexieId" | "hasData" | "hasThumbnailData">

// ─── Methods ───────────────────────────────────────────────────────────────────

export async function upsertPhoto(input: UpsertPhotoInput): Promise<void> {
  const { data, mime, thumbnailData, thumbnailMime, ...meta } = input
  const hasData = !!data
  const hasThumbnailData = !!thumbnailData

  try {
    await dexie.transaction("rw", [dexie.photos, dexie.photoBlobs], async () => {
      // ── metadata row in `photos` ──
      const metaRow = { ...meta, hasData, hasThumbnailData }
      const existing = await dexie.photos.where("id").equals(input.id).first()
      if (existing) {
        await dexie.photos.update(existing.dexieId, metaRow)
      } else {
        await dexie.photos.add(metaRow)
      }

      // ── binary payload in `photoBlobs` (only when a local binary exists) ──
      if (hasData || hasThumbnailData) {
        const blob = { photoId: input.id, sessionId: input.sessionId, data, mime, thumbnailData, thumbnailMime }
        const existingBlob = await dexie.photoBlobs.where("photoId").equals(input.id).first()
        if (existingBlob) {
          await dexie.photoBlobs.update(existingBlob.dexieId, blob)
        } else {
          await dexie.photoBlobs.add(blob)
        }
      }
    })
  } catch (error) {
    logDexieError(error)
  }
}

/** Load the binary payload for a single photo (upload + local render). */
export async function getPhotoBlob(photoId: string): Promise<PhotoBlob | undefined> {
  try {
    return await dexie.photoBlobs.where("photoId").equals(photoId).first()
  } catch (error) {
    logDexieError(error)
  }
}

export async function getPhotosByStep(sessionId: string, type: PhotoType): Promise<PhotoSlim[]> {
  try {
    const photos = await dexie.photos
      .where("sessionId")
      .equals(sessionId)
      .and((photo) => photo.type === type && !photo.deleted)
      .sortBy("createdAt")
    return photos
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function findAllPhotosForSession(sessionId: string): Promise<PhotoSlim[]> {
  try {
    const photos = await dexie.photos
      .where("sessionId")
      .equals(sessionId)
      .and((photo) => !photo.deleted)
      .toArray()
    return photos
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function findPhotoById(id: string): Promise<PhotoSlim | undefined> {
  try {
    return await dexie.photos.where("id").equals(id).first()
  } catch (error) {
    logDexieError(error)
  }
}

export async function softDeletePhoto(id: string): Promise<void> {
  try {
    await dexie.transaction("rw", [dexie.photos, dexie.photoBlobs], async () => {
      const photo = await dexie.photos.where("id").equals(id).first()
      if (!photo) return

      await dexie.photoBlobs.where("photoId").equals(id).delete()

      // The binary is gone, so the flags must stop claiming otherwise
      await dexie.photos.update(photo.dexieId, {
        deleted: true,
        updatedAt: new Date(),
        hasData: false,
        hasThumbnailData: false,
      })
    })
  } catch (error) {
    logDexieError(error)
  }
}

/** Uploaded photos deleted locally that still need their deletion synced to the server */
export async function getPendingPhotoDeletions(sessionId: string): Promise<PhotoSlim[]> {
  try {
    return await dexie.photos
      .where("sessionId")
      .equals(sessionId)
      .and((photo) => photo.deleted === true && !!photo.uploadedAt && !photo.deleteFailed)
      .toArray()
  } catch (error) {
    logDexieError(error)
    return []
  }
}

/**
 * Used by `useUploadStatus` to block finishing session before syncing all deleted photos to server first.
 */
export async function getUnsyncedPhotoDeletions(sessionId: string): Promise<PhotoSlim[]> {
  try {
    return await dexie.photos
      .where("sessionId")
      .equals(sessionId)
      .and((photo) => photo.deleted === true && !!photo.uploadedAt)
      .toArray()
  } catch (error) {
    logDexieError(error)
    return []
  }
}

/** Hard-delete the local photo row once its deletion has synced to the server (blob already removed on soft-delete) */
export async function hardDeletePhoto(id: string): Promise<void> {
  try {
    await dexie.photos.where("id").equals(id).delete()
  } catch (error) {
    logDexieError(error)
  }
}

export async function markPhotoDeleteFailed(id: string): Promise<void> {
  try {
    await dexie.photos.where("id").equals(id).modify({ deleteFailed: true })
  } catch (error) {
    logDexieError(error)
  }
}
